import { Component, Inject } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { ToastrService } from "ngx-toastr";
import { IncidentTrackerService } from "../../../shared/services/incident-tracker.service";

interface ResponseChoice {
  response: string;
  response_description: string;
}

interface ChecklistItem {
  name: string;
  required_documents: string[];
  expanded?: boolean;
  _docInput?: string;
}

interface EditableRequirement {
  requirement: string;
  checklist_items: ChecklistItem[];
  response_choices: ResponseChoice[];
  reference_documents: ReferenceDocVM[]; 
  turn_around_time: number | null; 
}

interface EditableSubdomainGroup {
  sub_domain: string;
  requirements: EditableRequirement[];
  expanded?: boolean;
}

interface EditableDomainGroup {
  domain: string;
  subdomains: EditableSubdomainGroup[];
  expanded?: boolean;
}

interface Selection {
  domainIndex: number | null;
  subIndex: number | null;
  reqIndex: number | null;
}

interface ReferenceDocVM {
  file?: File; // pending, not-yet-uploaded
  id?: number; // existing (edit mode / already uploaded)
  file_name: string;
  isExisting: boolean;
}

interface EditableRequirement {
  requirement: string;
  checklist_items: ChecklistItem[];
  response_choices: ResponseChoice[];
  reference_documents: ReferenceDocVM[]; // add this
}

@Component({
  selector: "audit-template-edit-dialog",
  templateUrl: "./audit-template-edit-dialog.component.html",
  styleUrls: ["./audit-template-edit-dialog.component.scss"],
})
export class AuditTemplateEditDialogComponent {
  frequencies: any;
  auditTypes: any;
  assessmentTags: any;
  usersList: any;
  responseOptions: any = [];
  selectedResponses: string[] = [];
  template: any = {};
  modalHeader: string = "";
  isCreateMode: boolean = false;
  step: 1 | 2 = 2;

  domainGroups: EditableDomainGroup[] = [];
  saving = false;
  selected: Selection = { domainIndex: null, subIndex: null, reqIndex: null };

  templateForm = new FormGroup({
    title: new FormControl("", [Validators.required]),
    description: new FormControl(""),
    applicability: new FormControl(""),
    focus_area: new FormControl(""),
    internal_assessor: new FormControl(""),
    external_assessor: new FormControl(""),
    is_external_assessment: new FormControl(false),
    frequency: new FormControl("", [Validators.required]),
    type: new FormControl("", [Validators.required]),
    responses: new FormControl([], [Validators.required]),
  });

  constructor(
    public dialogRef: MatDialogRef<AuditTemplateEditDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private readonly incidentTrackerService: IncidentTrackerService,
    private readonly toastrService: ToastrService
  ) {}

  ngOnInit() {
    this.isCreateMode = this.data?.isCreateMode === true;
    this.template = this.data.template;
    if (this.isCreateMode) {
      this.step = 1;
      this.getFrequencies();
      this.getAuditTypes();
      this.getUsers();
      const responsesControl = this.templateForm.get("responses");
      responsesControl?.clearValidators();
      responsesControl?.updateValueAndValidity();
      this.modalHeader = "Create Audit Template";
    } else {
      this.domainGroups = this.groupQuestions(this.template.questions || []);
      if (!this.domainGroups.length) {
        this.addDomain();
      }
      this.domainGroups.forEach((d) => (d.expanded = true));
      this.domainGroups.forEach((d) =>
        d.subdomains.forEach((s) => (s.expanded = true))
      );
      this.selectFirstAvailable();
      this.modalHeader = `Edit Template — ${this.template?.title}`;
    }
  }

  private groupQuestions(questions: any[]): EditableDomainGroup[] {
    const domains: EditableDomainGroup[] = [];
    questions.forEach((q: any) => {
      const requirement: EditableRequirement = {
        requirement: q.requirement,
        checklist_items: this.normalizeChecklistItems(
          q.checklist_items,
          q.required_documents
        ),
        response_choices: this.normalizeChoices(q.response_choices),
        reference_documents: (q.reference_documents || []).map((d: any) => ({
          id: d.id,
          file_name: d.file_name,
          isExisting: true,
        })),
        turn_around_time: q.turn_around_time ?? null,
      };

      let domainGroup = domains.find((d) => d.domain === q.domain);
      if (!domainGroup) {
        domainGroup = { domain: q.domain, subdomains: [], expanded: true };
        domains.push(domainGroup);
      }

      let subdomainGroup = domainGroup.subdomains.find(
        (s) => s.sub_domain === q.sub_domain
      );
      if (!subdomainGroup) {
        subdomainGroup = {
          sub_domain: q.sub_domain,
          requirements: [],
          expanded: true,
        };
        domainGroup.subdomains.push(subdomainGroup);
      }

      subdomainGroup.requirements.push(requirement);
    });
    return domains;
  }

  private normalizeDocs(docs: any): string[] {
    if (!docs) {
      return [];
    }
    if (Array.isArray(docs)) {
      return docs;
    }
    return String(docs)
      .split(",")
      .map((d) => d.trim())
      .filter((d) => !!d);
  }

  private normalizeChecklistItems(
    items: any,
    legacyDocs: any
  ): ChecklistItem[] {
    if (Array.isArray(items)) {
      return items.map((it: any) => ({
        name: it.name ?? "",
        required_documents: this.normalizeDocs(it.required_documents),
        expanded: true,
      }));
    }

    const migratedDocs = this.normalizeDocs(legacyDocs);

    return migratedDocs.length
      ? [
          {
            name: "",
            required_documents: migratedDocs,
            expanded: true,
          },
        ]
      : [];
  }

  private normalizeChoices(choices: any): ResponseChoice[] {
    if (!Array.isArray(choices)) {
      return [];
    }
    return choices.map((c: any) => ({
      response: c.response ?? "",
      response_description: c.response_description ?? "",
    }));
  }

  getResponseOptions() {
    this.incidentTrackerService
      .getAuditResponseChoices()
      .subscribe((resp: any) => {
        this.responseOptions = resp;
      });
  }

  getUsers() {
    this.incidentTrackerService.getUser().subscribe((resp: any) => {
      this.usersList = resp;
    });
  }

  getAuditTypes() {
    this.incidentTrackerService.getAuditTypes().subscribe((response) => {
      this.auditTypes = response;
    });
  }

  getFrequencies() {
    this.incidentTrackerService.getFrequencies().subscribe((response) => {
      this.frequencies = response;
    });
  }

  getChildTags(data: any) {
    this.assessmentTags = data;
  }

  removeResponse(index: number): void {
    this.selectedResponses.splice(index, 1);
    this.templateForm.get("responses")?.setValue(this.selectedResponses);
  }

  goNext() {
    if (this.templateForm.invalid) {
      this.templateForm.markAllAsTouched();
      this.toastrService.error(
        "Please fill in all required fields before continuing"
      );
      return;
    }
    if (!this.domainGroups.length) {
      this.addDomain();
    }
    this.step = 2;
  }

  goBack() {
    this.step = 1;
  }

  private emptyChecklistItem(): ChecklistItem {
    return { name: "", required_documents: [], expanded: true };
  }

  private emptyRequirement(): EditableRequirement {
    return {
      requirement: "",
      checklist_items: [],
      response_choices: [{ response: "", response_description: "" }],
      reference_documents: [],
      turn_around_time: null,
    };
  }

  private emptySubdomain(): EditableSubdomainGroup {
    return {
      sub_domain: "",
      requirements: [this.emptyRequirement()],
      expanded: true,
    };
  }

  private selectFirstAvailable() {
    for (let di = 0; di < this.domainGroups.length; di++) {
      const d = this.domainGroups[di];
      for (let si = 0; si < d.subdomains.length; si++) {
        const s = d.subdomains[si];
        if (s.requirements.length) {
          this.selected = { domainIndex: di, subIndex: si, reqIndex: 0 };
          return;
        }
      }
    }
    this.selected = { domainIndex: null, subIndex: null, reqIndex: null };
  }

  // ---------- tree structure edits ----------

  toggleDomain(domainGroup: EditableDomainGroup) {
    domainGroup.expanded = !domainGroup.expanded;
  }

  toggleSubdomain(subdomainGroup: EditableSubdomainGroup) {
    subdomainGroup.expanded = !subdomainGroup.expanded;
  }

  addDomain() {
    this.domainGroups.push({
      domain: "",
      subdomains: [this.emptySubdomain()],
      expanded: true,
    });
    const di = this.domainGroups.length - 1;
    this.selected = { domainIndex: di, subIndex: 0, reqIndex: 0 };
  }

  removeDomain(domainIndex: number) {
    this.domainGroups.splice(domainIndex, 1);
    if (this.selected.domainIndex === domainIndex) {
      this.selectFirstAvailable();
    } else if (
      this.selected.domainIndex !== null &&
      this.selected.domainIndex > domainIndex
    ) {
      this.selected.domainIndex--;
    }
  }

  addSubdomain(domainGroup: EditableDomainGroup, domainIndex: number) {
    domainGroup.subdomains.push(this.emptySubdomain());
    domainGroup.expanded = true;
    this.selected = {
      domainIndex,
      subIndex: domainGroup.subdomains.length - 1,
      reqIndex: 0,
    };
  }

  removeSubdomain(
    domainGroup: EditableDomainGroup,
    domainIndex: number,
    subIndex: number
  ) {
    domainGroup.subdomains.splice(subIndex, 1);
    if (
      this.selected.domainIndex === domainIndex &&
      this.selected.subIndex === subIndex
    ) {
      this.selectFirstAvailable();
    }
  }

  addRequirement(
    subdomainGroup: EditableSubdomainGroup,
    domainIndex: number,
    subIndex: number
  ) {
    subdomainGroup.requirements.push(this.emptyRequirement());
    subdomainGroup.expanded = true;
    this.selected = {
      domainIndex,
      subIndex,
      reqIndex: subdomainGroup.requirements.length - 1,
    };
  }

  removeRequirement(
    subdomainGroup: EditableSubdomainGroup,
    domainIndex: number,
    subIndex: number,
    reqIndex: number
  ) {
    subdomainGroup.requirements.splice(reqIndex, 1);
    if (
      this.selected.domainIndex === domainIndex &&
      this.selected.subIndex === subIndex &&
      this.selected.reqIndex === reqIndex
    ) {
      this.selectFirstAvailable();
    }
  }

  selectRequirement(domainIndex: number, subIndex: number, reqIndex: number) {
    this.selected = { domainIndex, subIndex, reqIndex };
  }

  isSelected(domainIndex: number, subIndex: number, reqIndex: number): boolean {
    return (
      this.selected.domainIndex === domainIndex &&
      this.selected.subIndex === subIndex &&
      this.selected.reqIndex === reqIndex
    );
  }

  get selectedRequirement(): EditableRequirement | null {
    const { domainIndex, subIndex, reqIndex } = this.selected;
    if (domainIndex === null || subIndex === null || reqIndex === null) {
      return null;
    }
    return (
      this.domainGroups[domainIndex]?.subdomains[subIndex]?.requirements[
        reqIndex
      ] || null
    );
  }

  get selectedDomain(): EditableDomainGroup | null {
    return this.selected.domainIndex === null
      ? null
      : this.domainGroups[this.selected.domainIndex] || null;
  }

  get selectedSubdomain(): EditableSubdomainGroup | null {
    if (this.selected.domainIndex === null || this.selected.subIndex === null) {
      return null;
    }
    return (
      this.domainGroups[this.selected.domainIndex]?.subdomains[
        this.selected.subIndex
      ] || null
    );
  }

  // ---------- checklist item edits (right pane) ----------

  toggleChecklistItem(item: ChecklistItem) {
    item.expanded = !item.expanded;
  }

  addChecklistItem(req: EditableRequirement) {
    req.checklist_items.push(this.emptyChecklistItem());
  }

  removeChecklistItem(req: EditableRequirement, itemIndex: number) {
    req.checklist_items.splice(itemIndex, 1);
  }

  addChecklistDocument(item: ChecklistItem) {
    const value = (item._docInput || "").trim();
    if (!value) {
      return;
    }
    item.required_documents.push(value);
    item._docInput = "";
  }

  removeChecklistDocument(item: ChecklistItem, docIndex: number) {
    item.required_documents.splice(docIndex, 1);
  }

  addResponseChoice(req: EditableRequirement) {
    req.response_choices.push({ response: "", response_description: "" });
  }

  removeResponseChoice(req: EditableRequirement, choiceIndex: number) {
    req.response_choices.splice(choiceIndex, 1);
  }

  checklistItemLabel(item: ChecklistItem): string {
    return item.name?.trim() || "New checklist item";
  }

  // ---------- validation (surfaced as indicators in the tree) ----------

  domainHasError(domainGroup: EditableDomainGroup): boolean {
    if (!domainGroup.domain?.trim() || !domainGroup.subdomains.length) {
      return true;
    }
    return domainGroup.subdomains.some((s) => this.subdomainHasError(s));
  }

  subdomainHasError(subdomainGroup: EditableSubdomainGroup): boolean {
    if (
      !subdomainGroup.sub_domain?.trim() ||
      !subdomainGroup.requirements.length
    ) {
      return true;
    }
    return subdomainGroup.requirements.some((r) => this.requirementHasError(r));
  }

  requirementHasError(req: EditableRequirement): boolean {
    const hasInvalidChecklistItem = req.checklist_items.some(
      (item) => !item.name?.trim()
    );
    const hasInvalidTurnAroundTime =
      req.turn_around_time !== null &&
      req.turn_around_time !== undefined &&
      (isNaN(req.turn_around_time) || req.turn_around_time <= 0);

    return (
      !req.requirement?.trim() ||
      !req.response_choices.length ||
      req.response_choices.some((c) => !c.response?.trim()) ||
      hasInvalidChecklistItem ||
      hasInvalidTurnAroundTime
    );
  }

  requirementLabel(req: EditableRequirement): string {
    return req.requirement?.trim() || "New requirement";
  }

  isValid(): boolean {
    if (!this.domainGroups.length) {
      return false;
    }
    return !this.domainGroups.some((d) => this.domainHasError(d));
  }

  onReferenceFileSelected(event: any, req: EditableRequirement) {
    const files: FileList = event.target.files;
    if (!files?.length) return;
    for (let file of Array.from(files)) {
      const exists = req.reference_documents.some(
        (d) => d.file_name === file.name
      );
      if (exists) {
        this.toastrService.error(`File ${file.name} already added`);
        continue;
      }
      req.reference_documents.push({
        file,
        file_name: file.name,
        isExisting: false,
      });
    }
    event.target.value = "";
  }

  removeReferenceDocument(req: EditableRequirement, index: number) {
    req.reference_documents.splice(index, 1);
  }

  save() {
    if (!this.isValid()) {
      this.toastrService.error(
        "Domain, Subdomain, Requirement, and at least one Response are required for every entry"
      );
      return;
    }

    const flatQuestions: any[] = [];
    const filesByIndex: File[][] = [];

    this.domainGroups.forEach((domainGroup) => {
      domainGroup.subdomains.forEach((subdomainGroup) => {
        subdomainGroup.requirements.forEach((r) => {
          filesByIndex.push(
            r.reference_documents
              .filter((d) => !d.isExisting)
              .map((d) => d.file!)
          );
          flatQuestions.push({
            domain: domainGroup.domain.trim(),
            subdomain: subdomainGroup.sub_domain.trim(),
            requirement: r.requirement.trim(),
            turn_around_time: r.turn_around_time ?? null,
            checklist_items: r.checklist_items.map((ci) => ({
              name: ci.name.trim(),
              required_documents: ci.required_documents,
            })),
            response_choices: r.response_choices.map((c) => ({
              response: c.response.trim(),
              response_description: (c.response_description || "").trim(),
            })),
            reference_documents: r.reference_documents
              .filter((d) => d.isExisting)
              .map((d) => d.id),
          });
        });
      });
    });

    const formData = new FormData();
    formData.append("questions", JSON.stringify(flatQuestions));
    filesByIndex.forEach((files, i) => {
      files.forEach((file) =>
        formData.append(`reference_documents_${i}`, file)
      );
    });

    this.saving = true;

    if (!this.isCreateMode) {
      this.incidentTrackerService
        .updateAuditTemplate(this.template.id, formData)
        .subscribe(
          (updatedTemplate: any) => {
            this.saving = false;
            this.dialogRef.close(updatedTemplate);
          },
          (error: any) => {
            this.saving = false;
            this.toastrService.error(
              error?.error?.error || "Failed to update template"
            );
          }
        );
      return;
    }

    const formValue = this.templateForm.value;
    formData.append("title", formValue.title);
    formData.append("description", formValue.description || "");
    formData.append("applicability", formValue.applicability || "");
    formData.append("focus_area", formValue.focus_area || "");
    if (formValue.internal_assessor)
      formData.append("internal_assessor", formValue.internal_assessor);
    formData.append("external_assessor", formValue.external_assessor || "");
    formData.append(
      "is_external_assessment",
      String(!!formValue.is_external_assessment)
    );
    formData.append("frequency", formValue.frequency);
    formData.append("type", formValue.type);
    formData.append("responses", JSON.stringify(this.selectedResponses));

    this.incidentTrackerService.createAuditTemplate(formData).subscribe(
      (response: any) => {
        this.saving = false;
        this.dialogRef.close({ response, selectedTags: this.assessmentTags });
      },
      (error: any) => {
        this.saving = false;
        this.toastrService.error(
          error?.error?.error || "Failed to update template"
        );
      }
    );
  }

  closeModal(id: any): void {
    this.dialogRef.close(id);
  }
}
